import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";

// Kitchen PIN status for staff at the manager's sites. PINs are never shown; managers issue a
// single-use setup code (also for staff without a personal login) that the person uses on the tablet.
export function ManagePins({ client, onBack, dark = true }) {
  const [people, setPeople] = useState([]);
  const [code, setCode] = useState(null);
  const load = () => client.get("/tablet/people").then((r) => setPeople(r.data || [])).catch(() => toast.error("Could not load staff"));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const issue = async (p) => {
    try { const r = await client.post(`/tablet/people/${p.person_id}/pin-setup`); setCode({ name: p.display_name, ...r.data }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create a setup code"); }
  };
  const access = async (p, enabled) => {
    try { await client.post(`/tablet/people/${p.person_id}/access`, { enabled }); load(); }
    catch { toast.error("Could not change access"); }
  };
  const card = dark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-800";
  return (
    <div className="p-4 sm:p-6" data-testid="tablet-manage-pins-view">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold">Staff kitchen PINs</h2>
        {onBack && <button onClick={onBack} data-testid="pins-done" className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm">Done</button>}
      </div>
      <p className={`text-sm mb-4 ${dark ? "text-slate-400" : "text-slate-500"}`}>Staff create their own PIN in CaterCTRL (My profile, then Kitchen PIN). For anyone without a login, or a forgotten PIN, create a one-time setup code and give it to that person. They enter it on the tablet with a new PIN. PINs are never shown to anyone.</p>
      <div className="space-y-3">
        {people.length === 0 && <p className="text-sm text-slate-500">No active staff at your sites.</p>}
        {people.map((p) => (
          <div key={p.person_id} className={`rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${card}`} data-testid={`pin-person-${p.person_id}`}>
            <div>
              <p className="font-semibold">{p.display_name} {!p.linked && <span className="text-xs text-amber-500">(no login: can set a PIN, but can't record until invited)</span>}</p>
              <p className="text-xs text-slate-500" data-testid={`pin-status-${p.person_id}`}>{p.has_pin ? "PIN set" : "No PIN"} · {p.kitchen_tablet_access ? "Tablet access on" : "Tablet access off"}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => issue(p)} data-testid={`pin-setup-${p.person_id}`} className="px-3 py-2 rounded-lg bg-brand/20 text-brand text-sm flex items-center gap-1"><KeyRound size={14} />{p.has_pin ? "Reset with setup code" : "Create setup code"}</button>
              {p.kitchen_tablet_access && <button onClick={() => access(p, false)} data-testid={`pin-access-off-${p.person_id}`} className="px-3 py-2 rounded-lg bg-slate-700/40 text-sm">Turn off</button>}
            </div>
          </div>
        ))}
      </div>
      {code && (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 flex items-center justify-center p-6" data-testid="pin-setup-code-modal">
          <div className="w-full max-w-sm bg-slate-900 text-white rounded-3xl p-7 border border-slate-700">
            <div className="flex justify-between items-center"><p className="text-slate-300">Setup code for {code.name}</p><button onClick={() => setCode(null)} data-testid="pin-setup-code-close"><X size={20} /></button></div>
            <p className="text-4xl font-bold tracking-[0.3em] text-center my-6" data-testid="pin-setup-code">{code.setup_code}</p>
            <p className="text-sm text-slate-400">Give this to {code.name} only. On the tablet they tap their name, choose "I have a setup code", enter it and pick a new PIN. It works once and expires in {code.expires_in_minutes} minutes. Their old PIN no longer works.</p>
          </div>
        </div>
      )}
    </div>
  );
}
